<template>
  <div>
    <!-- Home button -->
    <div class="position-absolute m-3" style="z-index: 1000;">
      <router-link to="/" class="btn btn-secondary">Home</router-link>
    </div>
    <div class="container d-flex justify-content-center align-items-center" style="min-height: 100vh;">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header bg-info text-white text-center">
            <h3 class="mb-0">Twitter Uploader</h3>
            <small>Tweet with Video(s) for Golingo</small>
          </div>
          <div class="card-body">
            <!-- Authentication Section -->
            <div id="tw-auth-section" class="mb-4 text-center">
              <button v-if="!isTWAuthenticated" id="tw-login-button" class="btn btn-primary custom-btn" @click="handleTWLogin">
                Login with Twitter
              </button>
              <button v-if="isTWAuthenticated" id="tw-logout-button" class="btn btn-danger custom-btn" @click="handleTWLogout">
                Logout
              </button>
              <div id="tw-auth-status" class="mt-2">{{ twAuthStatus }}</div>
            </div>
            <!-- Upload Section (only visible when authenticated) -->
            <div v-if="isTWAuthenticated" id="tw-upload-section">
              <!-- Video Upload Form: Show if the uploaded count is less than 4 -->
              <div v-if="mediaIds.length < 4">
                <form id="tw-video-upload-form">
                  <div class="mb-3">
                    <input type="file" id="tw-video-file" ref="twVideoFile" accept="video/*" class="form-control"/>
                  </div>
                  <div class="d-grid gap-2 mb-3">
                    <button type="button" class="btn btn-primary custom-btn" @click="uploadTWVideoChunked">
                      Upload Video
                    </button>
                  </div>
                </form>
              </div>
              <!-- Display list of uploaded videos -->
              <div v-if="mediaIds.length > 0" class="mb-4">
                <h5>Uploaded Videos:</h5>
                <ol>
                  <li v-for="(fileName, index) in uploadedFileNames" :key="index">{{ fileName }}</li>
                </ol>
                <!-- If less than 4 videos are uploaded, allow uploading additional ones -->
                <div v-if="mediaIds.length < 4">
                  <button type="button" class="btn btn-secondary custom-btn" @click="clearFileInput">Choose another file</button>
                </div>
              </div>
              <!-- Tweet Posting Form -->
              <div>
                <form id="tw-tweet-form">
                  <div class="mb-3">
                    <textarea id="tw-tweet-text" v-model="tweetText" rows="4" class="form-control" placeholder="Enter your tweet text"></textarea>
                  </div>
                  <div class="d-grid gap-2 mb-3">
                    <button type="button" class="btn btn-primary custom-btn" @click="postTweet">
                      Post Tweet with Video(s)
                    </button>
                  </div>
                </form>
              </div>
              <!-- Spinner -->
              <div id="tw-spinner" class="text-center" v-if="showTWSpinner">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Uploading...</span>
                </div>
                <p>Uploading video, please wait...</p>
              </div>
            </div>
          </div>
          <div class="card-footer text-center text-muted">
            &copy; 2023 Golingo - Twitter Module
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TwitterUploader",
  data() {
    return {
      tweetText: "",
      twAuthStatus: "Not logged in.",
      isTWAuthenticated: false,
      showTWSpinner: false,
      mediaIds: [],           // Array that holds the uploaded video media IDs
      uploadedFileNames: []   // Array to display file names that were uploaded
    }
  },
  mounted() {
    // Retrieve and use the real user_id from localStorage.
    this.initializeTwitterUploader();
  },
  methods: {
    // Utility functions:
    generateRandomString(length) {
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
      let text = '';
      for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }
      return text;
    },
    generateState(length) {
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let text = '';
      for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
      }
      return text;
    },
    async generateCodeChallenge(codeVerifier) {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digest = await crypto.subtle.digest('SHA-256', data);
      let base64String = btoa(String.fromCharCode(...new Uint8Array(digest)));
      return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    },
    getQueryParams() {
      const params = {};
      const queryString = window.location.search.substring(1);
      const regex = /([^&=]+)=([^&]*)/g;
      let m;
      while ((m = regex.exec(queryString))) {
        params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
      }
      return params;
    },
    initializeTwitterUploader() {
      // Use the real user_id stored during login.
      const user_id = localStorage.getItem("userEmail") || "test_user";
      console.log(user_id);
      const storedToken = localStorage.getItem('tw_access_token');
      const tokenExpiry = localStorage.getItem('tw_token_expiry');
      if (storedToken && tokenExpiry && new Date().getTime() < tokenExpiry) {
        this.twAuthStatus = "Logged in!";
        this.isTWAuthenticated = true;
      } else {
        // Token is missing or expired; fetch a new one from the API using the real user_id.
        fetch("https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/twitter-user-get-token", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ user_id: user_id })
        })
        .then(response => response.json())
        .then(data => {
          if (data.statusCode === 200 && data.body && data.body.access_token) {
            const newToken = data.body.access_token;
            localStorage.setItem("tw_access_token", newToken);
            const newExpiry = new Date().getTime() + 3600000; // 1 hour
            localStorage.setItem("tw_token_expiry", newExpiry);
            this.twAuthStatus = "Logged in!";
            this.isTWAuthenticated = true;
          } else {
            console.error("Failed to get valid token. Response:", data);
          }
        })
        .catch(error => {
          console.error("Error fetching Twitter token:", error);
        });
      }
    },
    handleTWLogin() {
      const codeVerifier = this.generateRandomString(64);
      // Use the real user_id from localStorage.
      const stateObject = {
        code_verifier: codeVerifier,
        user_id: localStorage.getItem("userEmail") || "test_user",
        client_url: window.location.origin + window.location.pathname
      };
      console.log(stateObject);
      const state = btoa(JSON.stringify(stateObject));
      localStorage.setItem('tw_pkce_code_verifier', codeVerifier);
      this.generateCodeChallenge(codeVerifier).then(codeChallenge => {
        console.log("codeVerifier:", codeVerifier);
        const authUrl = `${TW_AUTH_ENDPOINT}?response_type=code` +
            `&client_id=${TW_CLIENT_ID}` +
            `&redirect_uri=${encodeURIComponent(TW_REDIRECT_URI)}` +
            `&scope=${TW_SCOPES}` +
            `&state=${state}` +
            `&code_challenge=${codeChallenge}` +
            `&code_challenge_method=S256`;
        window.location.href = authUrl;
      });
    },
    handleTWLogout() {
      localStorage.removeItem('tw_access_token');
      localStorage.removeItem('tw_token_expiry');
      this.twAuthStatus = "Not logged in.";
      this.isTWAuthenticated = false;
      this.mediaIds = [];
      this.uploadedFileNames = [];
      this.tweetText = "";
      if(this.$refs.twVideoFile){
        this.$refs.twVideoFile.value = "";
      }
    },
    clearFileInput() {
      if (this.$refs.twVideoFile) {
        this.$refs.twVideoFile.value = "";
      }
    },
    uploadTWVideoChunked() {
      const access_token = localStorage.getItem('tw_access_token');
      if (!access_token) {
        alert("Not authenticated.");
        return;
      }
      const file = this.$refs.twVideoFile.files[0];
      if (!file) {
        alert("Please select a video file.");
        return;
      }
      this.showTWSpinner = true;
      
      const lambdaUploadEndpoint = 'https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/twitter_upload_video';
      const initParams = new URLSearchParams();
      initParams.append('command', 'INIT');
      initParams.append('total_bytes', file.size);
      initParams.append('media_type', file.type);
      initParams.append('media_category', 'tweet_video');
      initParams.append('access_token', access_token);
      
      fetch(lambdaUploadEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: initParams.toString()
      })
      .then(response => response.json())
      .then(initData => {
        if (!initData.data || !initData.data.id) {
          throw new Error("INIT call failed: " + JSON.stringify(initData));
        }
        const currentMediaId = initData.data.id;
        const chunkSize = 5 * 1024 * 1024;   // 5 MB chunks
        const totalChunks = Math.ceil(file.size / chunkSize);
        let currentChunk = 0;
        
        const uploadNextChunk = () => {
          if (currentChunk < totalChunks) {
            const start = currentChunk * chunkSize;
            const end = Math.min(start + chunkSize, file.size);
            const blob = file.slice(start, end);
            const reader = new FileReader();
            reader.onload = (event) => {
              const base64Data = event.target.result.split(',')[1];
              const appendPayload = {
                command: 'APPEND',
                media_id: currentMediaId,
                segment_index: currentChunk,
                access_token: access_token,
                media: base64Data
              };
              fetch(lambdaUploadEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(appendPayload)
              })
              .then(response => {
                if (response.status === 204) {
                  currentChunk++;
                  uploadNextChunk();
                } else {
                  throw new Error('Network response was not ok during APPEND.');
                }
              })
              .catch(err => {
                console.error("Error during APPEND for chunk " + currentChunk + ":", err);
                this.showTWSpinner = false;
              });
            };
            reader.readAsDataURL(blob);
          } else {
            finalizeUpload(currentMediaId);
          }
        };
        
        const finalizeUpload = (currentMediaId) => {
          const finalizeParams = new URLSearchParams();
          finalizeParams.append('command', 'FINALIZE');
          finalizeParams.append('media_id', currentMediaId);
          finalizeParams.append('access_token', access_token);
          
          fetch(lambdaUploadEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: finalizeParams.toString()
          })
          .then(response => response.json())
          .then(finalData => {
            this.mediaIds.push(currentMediaId);
            console.log(this.mediaIds);
            this.uploadedFileNames.push(file.name);
            this.showTWSpinner = false;
            alert("Video uploaded successfully!");
            if(this.$refs.twVideoFile) {
              this.$refs.twVideoFile.value = "";
            }
          })
          .catch(err => {
            console.error("Error during FINALIZE:", err);
            this.showTWSpinner = false;
          });
        };
        
        uploadNextChunk();
      })
      .catch(err => {
        console.error("Error during INIT:", err);
        this.showTWSpinner = false;
      });
    },
    postTweet() {
      const tweetTextTrimmed = this.tweetText.trim();
      if (tweetTextTrimmed === "") {
        alert("Please enter some text for your tweet.");
        return;
      }
      if (!this.mediaIds.length) {
        alert("No video uploaded yet.");
        return;
      }
      const access_token = localStorage.getItem('tw_access_token');
      if (!access_token) {
        alert("Not authenticated.");
        return;
      }
      const tweetPayload = {
        access_token: access_token,
        text: tweetTextTrimmed,
        media_ids: this.mediaIds
      };
      const postTweetEndpoint = 'https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/twitter_post_tweet';
      fetch(postTweetEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tweetPayload)
      })
      .then(response => response.json())
      .then(data => {
        alert("Tweet posted successfully!");
        this.tweetText = "";
        this.mediaIds = [];
        this.uploadedFileNames = [];
        if (this.$refs.twVideoFile) {
          this.$refs.twVideoFile.value = "";
        }
      })
      .catch(err => {
        console.error("Error posting tweet:", err);
      });
    }
  }
}

// Twitter configuration constants
const TW_CLIENT_ID = 'aTFPSUFOLWhHYUltYzNYczRhSlg6MTpjaQ';
let TW_REDIRECT_URI = `https://806d425v42.execute-api.us-east-1.amazonaws.com/prod/twitter-user-consent-callback`;
const TW_AUTH_ENDPOINT = 'https://twitter.com/i/oauth2/authorize';
const TW_SCOPES = encodeURIComponent('tweet.read tweet.write media.write users.read offline.access');
</script>

<style scoped>
@import "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";

body {
  background: #f8f9fa;
  font-family: sans-serif;
  margin: 0;
}
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.custom-btn {
  min-width: 150px;
}
#tw-spinner {
  margin-top: 1rem;
}
</style>